import React, { useCallback, useEffect, useMemo, useState } from "react";
import { personalizationApi } from "@/api/personalizationClient";
import { useAuth } from "@/contexts/AuthContext";

/**
 * CareLogix™ ORACLE — Personalization Context (Phase 31).
 *
 * Single source of truth for the authenticated user's personalization profile.
 * Listens for auth changes and exposes:
 *   - profile (Phase 31 profile object)
 *   - relationship (greeting, tone, archetype, rules, format)
 *   - options (canonical option ladders)
 *   - refresh / patch / postEvent helpers
 *   - loading / err states
 *
 * Strictly additive. No removal of any existing context or provider.
 */

const Ctx = React.createContext({
  profile:      null,
  relationship: null,
  options:      null,
  loading:      false,
  err:          "",
  refresh:      () => Promise.resolve(),
  patch:        async () => null,
  postEvent:    () => {},
});

export function PersonalizationProvider({ children }) {
  const auth = useAuth();
  const isAuthed = !!auth?.isAuthenticated;
  const [profile, setProfile] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchOptions = useCallback(async () => {
    try {
      const { data } = await personalizationApi.options();
      setOptions(data);
    } catch (_) { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthed) {
      setProfile(null); setRelationship(null);
      return;
    }
    setLoading(true); setErr("");
    try {
      const { data } = await personalizationApi.getProfile();
      setProfile(data.profile);
      setRelationship(data.relationship);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "profile_load_failed");
    } finally { setLoading(false); }
  }, [isAuthed]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  useEffect(() => { refresh(); }, [refresh]);

  // Re-sync on auth change broadcast
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onAuth = () => refresh();
    window.addEventListener("carelogix:auth-changed", onAuth);
    return () => window.removeEventListener("carelogix:auth-changed", onAuth);
  }, [refresh]);

  const patch = useCallback(async (body) => {
    if (!isAuthed) return null;
    setLoading(true); setErr("");
    try {
      const { data } = await personalizationApi.patchProfile(body || {});
      setProfile(data.profile);
      setRelationship(data.relationship);
      return data;
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "profile_save_failed");
      return null;
    } finally { setLoading(false); }
  }, [isAuthed]);

  const postEvent = useCallback((type, payload) => {
    if (!isAuthed) return;
    try { personalizationApi.postEvent(type, payload).catch(() => {}); } catch (_) { /* ignore */ }
  }, [isAuthed]);

  const value = useMemo(() => ({
    profile, relationship, options, loading, err, refresh, patch, postEvent,
    isAuthed,
  }), [profile, relationship, options, loading, err, refresh, patch, postEvent, isAuthed]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePersonalization() { return React.useContext(Ctx); }
